import { z } from 'zod';
import { isValidSourceAddress } from './provisioning.js';
import { intToIp, ipToInt } from './tools.js';

/**
 * Auto-provisioning static routes for newly added resources.
 *
 * Mirrors the by-hand pattern already used to reach monitored devices over a
 * VPN gateway (a fixed gateway/interface, one /32 route per device, additive
 * only) — except instead of an operator editing a host script by hand for
 * every new device, the dashboard runs the same `ip route add` itself over
 * SSH to the host, the moment a resource with an address is created.
 */

const IFACE_RE = /^[A-Za-z0-9_.-]{1,15}$/;

export function isValidGatewayAddress(value: string): boolean {
  return isValidSourceAddress(value) && !value.includes('/');
}

export function isValidInterfaceName(value: string): boolean {
  return IFACE_RE.test(value);
}

export const DEFAULT_STATIC_ROUTE_GATEWAY = '19.18.17.1';
export const DEFAULT_STATIC_ROUTE_IFACE = 'tap0';

export const hostRoutingSettingsUpdateSchema = z.object({
  enabled: z.boolean(),
  host: z.string().min(1).optional(),
  port: z.number().int().min(1).max(65535).optional(),
  username: z.string().min(1).optional(),
  password: z.string().optional(),
  /** Drop a previously saved password without supplying a new one. */
  clearPassword: z.boolean().optional(),
  gateway: z.string().refine(isValidGatewayAddress, 'Gateway must be a plain IPv4 address').optional(),
  iface: z.string().refine(isValidInterfaceName, 'Interface name looks invalid').optional(),
});
export type HostRoutingSettingsUpdate = z.infer<typeof hostRoutingSettingsUpdateSchema>;

export interface HostRoutingSettings {
  enabled: boolean;
  host: string | null;
  port: number;
  username: string | null;
  hasPassword: boolean;
  gateway: string;
  iface: string;
  lastAppliedAt: number | null;
  lastError: string | null;
  updatedAt: number | null;
}

/**
 * Prefix the backfill routes at.
 *
 * A /32 per registered address only ever reaches the addresses somebody
 * remembered to register: a subnet's own gateway, a switch nobody entered,
 * a device racked last week all stay unreachable from the server even though
 * the operator's own PC reaches them fine (192.168.24.1 being the case that
 * prompted this). Routing the /24 each registered address sits in brings its
 * neighbours along, which is how these routes get maintained by hand anyway.
 */
export const STATIC_ROUTE_SUBNET_PREFIX = 24;

/**
 * The subnet a plain IPv4 address belongs to: `192.168.24.7` -> `192.168.24.0/24`.
 * Null for anything that isn't a bare IPv4 address.
 */
export function subnetForAddress(address: string, prefix: number = STATIC_ROUTE_SUBNET_PREFIX): string | null {
  if (address.includes('/') || !isValidSourceAddress(address)) return null;
  if (!Number.isInteger(prefix) || prefix < 8 || prefix > 32) return null;
  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  return `${intToIp((ipToInt(address) & mask) >>> 0)}/${prefix}`;
}

export interface HostRouteApplyResult {
  resourceId: string;
  resourceName: string;
  address: string;
  ok: boolean;
  alreadyPresent: boolean;
  output: string;
}

/**
 * One route per subnet the estate actually occupies, with the registered
 * addresses that put it on the list — the backfill's unit of work.
 */
export interface HostRouteSubnetApplyResult {
  subnet: string;
  /** How many registered addresses fall inside it. */
  addressCount: number;
  /** Resources covered, for display; truncated by the server. */
  resourceNames: string[];
  /** Source address pinned onto the route, if one was needed and found. */
  source: string | null;
  /** False when the host already owned this prefix and we left it untouched. */
  managed: boolean;
  /**
   * Set when this route changes where traffic already went — it overrides a
   * broader route pointing somewhere else, or the host owns the prefix and we
   * stood down. Null when nothing else on the host claims these addresses.
   */
  warning: string | null;
  /** Set when the subnet answered on none of the interface's addresses — a
   *  missing return path at the far end, not a routing overlap here. */
  unreachable?: string | null;
  ok: boolean;
  alreadyPresent: boolean;
  output: string;
}

/**
 * `ip route` for one destination, optionally pinning the source address.
 *
 * The source matters more than it looks. A route `via` a gateway leaves the
 * kernel to pick a source from the gateway's own subnet, and on a tunnel that
 * carries two addresses that is a coin toss the far side may not answer:
 * 192.168.24.1 replies to 192.168.50.200 and ignores 19.18.17.90, while
 * 192.168.21.1 does the opposite. `src` is what settles it per route.
 */
function routeCommand(verb: 'add' | 'replace', destination: string, gateway: string, iface: string, source?: string): string {
  return `ip route ${verb} ${destination} via ${gateway} dev ${iface} onlink${source ? ` src ${source}` : ''}`;
}

/** The exact, additive command this feature runs for one destination. */
export function routeAddCommand(destination: string, gateway: string, iface: string, source?: string): string {
  return routeCommand('add', destination, gateway, iface, source);
}

/**
 * Re-state a route this feature already owns, to pin a source onto it. Only
 * ever aimed at a route whose gateway and device already match ours — never
 * at one the host had for its own reasons.
 */
export function routeReplaceCommand(destination: string, gateway: string, iface: string, source?: string): string {
  return routeCommand('replace', destination, gateway, iface, source);
}

/**
 * Where the dashboard records the routes it manages, for the host's own boot
 * script to replay. `ip route` only edits the running kernel; without this
 * file every subnet and source pin is lost on the next reboot or tunnel
 * restart, and the operator is back to clicking Sync to get the estate back.
 */
export const HOST_ROUTE_STATE_DIR = '/etc/itresources/vpn-routes.d';
export const HOST_ROUTE_STATE_FILE = `${HOST_ROUTE_STATE_DIR}/dashboard.conf`;

/**
 * The state file's contents: one route per line, `subnet gateway iface [src]`,
 * deliberately plain enough for the boot script to read with `read` and for a
 * human to check at a glance.
 */
export function hostRouteStateFile(
  routes: { subnet: string; source: string | null }[],
  gateway: string,
  iface: string,
): string {
  const lines = [
    '# Written by the IT Resources dashboard — edits here are overwritten.',
    '# Replayed at boot by /usr/local/sbin/itr-vpn-routes.sh.',
    '# subnet gateway interface [source]',
    ...routes.map((r) => `${r.subnet} ${gateway} ${iface}${r.source ? ` ${r.source}` : ''}`),
  ];
  return `${lines.join('\n')}\n`;
}

/** A host route to a single address — what adding one resource provisions. */
export function staticRouteCommand(address: string, gateway: string, iface: string): string {
  return routeAddCommand(`${address}/32`, gateway, iface);
}
